import { LIVE_PRODUCTION_URLS, jwtNotExpired, sameSiteApiUrl } from '@self-restaurant/shared'
/**
 * Cliente WebSocket de tempo real (cozinha/garçom).
 *
 * Conecta em /realtime/ws da API (mesma origem), que valida o JWT e encaminha
 * ao Durable Object do tenant. Reconexão automática com backoff exponencial —
 * o SLA de produto é delay ≤ 5s entre evento e exibição.
 */
import { useEffect, useRef, useState } from 'react'
import { getToken, getTenantSlug, getTokenPayload, effectiveRoles } from './auth.js'
import { refreshSession } from './api.js'
import {
  getImpersonationSession,
  isImpersonationActive,
  onImpersonationChange,
} from './impersonation.js'
import type { WsEvent } from '@self-restaurant/shared'

export type RealtimeStatus = 'connecting' | 'open' | 'closed'

type RealtimeOptions = {
  role: string
  tableId?: string
  onEvent: (event: WsEvent) => void
}

/** Hook: mantém uma conexão WS viva enquanto o componente existe. */
export function useRealtime({ role, tableId, onEvent }: RealtimeOptions) {
  const [status, setStatus] = useState<RealtimeStatus>('connecting')
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent

  const slug = getTenantSlug()

  useEffect(() => {
    let ws: WebSocket | null = null
    let closedByUs = false
    let retrySec = 1
    let timer: number | undefined

    /** #430: sessão da plataforma terminou — não religar em loop. */
    function sessionBlocksReconnect(): boolean {
      const payload = getTokenPayload()
      const roles = effectiveRoles(payload)
      if (!roles.includes('platform_owner')) return false
      const session = getImpersonationSession()
      // Sessão local expirada (ainda no ecrã antes do redirect) → para
      if (session && !isImpersonationActive()) return true
      // Claim no JWT mas estado local já limpo (stop/expiração) → para
      if (payload?.impersonating && !session) return true
      if (session && session.tenant_slug !== slug) return true
      return false
    }

    function connect() {
      const token = getToken()
      if (!token || closedByUs) return
      if (sessionBlocksReconnect()) {
        closedByUs = true
        setStatus('closed')
        return
      }
      // Token expirado: renova antes (o WS com token vencido só daria 401).
      if (!jwtNotExpired(token)) {
        void refreshSession().then((ok) => {
          if (ok && !closedByUs && !sessionBlocksReconnect()) connect()
        })
        return
      }

      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
      const params = new URLSearchParams({ tenant: slug, token, role })
      if (tableId) params.set('tableId', tableId)
      // Produção: direto na origem da API (o proxy do admin não repassa
      // upgrade de WebSocket). Dev: mesma origem via proxy do Vite.
      const host = import.meta.env.DEV
        ? window.location.host
        : new URL(
            sameSiteApiUrl(
              (import.meta.env.VITE_API_URL as string | undefined) ?? LIVE_PRODUCTION_URLS.api,
              window.location.hostname,
            ),
          ).host
      ws = new WebSocket(`${proto}://${host}/realtime/ws?${params}`)
      setStatus('connecting')

      ws.onopen = () => {
        retrySec = 1
        setStatus('open')
      }
      ws.onmessage = (msg) => {
        try {
          onEventRef.current(JSON.parse(msg.data) as WsEvent)
        } catch {
          // payload inválido — ignora
        }
      }
      ws.onclose = () => {
        setStatus('closed')
        if (!closedByUs && !sessionBlocksReconnect()) {
          timer = window.setTimeout(connect, retrySec * 1000)
          retrySec = Math.min(retrySec * 2, 30)
        }
      }
      ws.onerror = () => {
        ws?.close()
      }
    }

    const unsub = onImpersonationChange(() => {
      if (sessionBlocksReconnect()) {
        closedByUs = true
        if (timer) window.clearTimeout(timer)
        ws?.close()
        setStatus('closed')
      }
    })

    connect()
    return () => {
      closedByUs = true
      unsub()
      if (timer) window.clearTimeout(timer)
      ws?.close()
    }
    // slug/role/tableId são estáveis por tela; token é relido a cada tentativa
  }, [slug, role, tableId])

  return status
}
